import { Injectable } from '@angular/core';
import { Claim, ClaimBundleType } from '@domgen/dgx-components';
import { claimActions, ClaimFacade } from '@domgen/data-access-claims';
import { Api, PageSection, PageSectionId } from '@domgen/dgx-components';
import { ClaimHelperService } from '@domgen/data-access-claims';

export enum PageSectionHeading {
  ClaimTypeAndFaultTypeQuestions = 'Describe the fault',
  FaultDetailsQandA = 'Tell us a bit more',
  ContactDetails = 'Check your details',
  RepairOption = 'Your repair',
  CallbackBookingWidget = 'Choose your repair',
}

@Injectable()
export class RepairDetailsService {
  readonly allClaimTypeCodes = ['BD', 'AD', 'ASV', 'BDBOCO', 'BDBOSY'];
  readonly breakdownClaimTypeCodes = ['BD', 'BDBOCO', 'BDBOSY'];
  readonly sectionSchemaDefault: PageSection[] = [
    {
      id: PageSectionId.ClaimTypeAndFaultTypeQuestions,
      title: PageSectionHeading.ClaimTypeAndFaultTypeQuestions,
      completed: false,
      active: false,
    },
    {
      id: PageSectionId.FaultDetailsQandA,
      title: PageSectionHeading.FaultDetailsQandA,
      completed: false,
      active: false,
    },
    {
      id: PageSectionId.ContactDetails,
      title: PageSectionHeading.ContactDetails,
      completed: false,
      active: false,
    },
    {
      id: PageSectionId.RepairOption,
      title: PageSectionHeading.RepairOption,
      completed: false,
      active: false,
    },
    {
      id: PageSectionId.CallbackBookingWidget,
      title: PageSectionHeading.CallbackBookingWidget,
      completed: false,
      active: false,
    },
  ];

  constructor(
    private claimFacade: ClaimFacade,
    private _claimHelper: ClaimHelperService
  ) {}

  showInWarrantyNotice(claim: Claim, selectedClaimTypeCode: string): boolean {
    const isBreakdown = this.breakdownClaimTypeCodes.includes(
      selectedClaimTypeCode
    );
    return isBreakdown && (claim?.getData?.PlanInWarrantyPeriod as boolean);
  }

  showplanInWaitPeriodNotice(
    claim: Claim,
    selectedClaimTypeCode: string
  ): boolean {
    return (
      (claim?.getData?.PlanInWaitPeriod as boolean) &&
      !this.showInWarrantyNotice(claim, selectedClaimTypeCode)
    );
  }

  getSectionSchemaFlags(
    sectionSchema: PageSection[],
    claim: Claim
  ): PageSection[] {
    // set completed and active flags for each section
    return sectionSchema.map((section) => {
      switch (section.id) {
        case PageSectionId.ClaimTypeAndFaultTypeQuestions:
          section.completed = claim.created as boolean;
          section.active = !claim.created;
          break;
        case PageSectionId.FaultDetailsQandA:
          section.completed = claim.accepted as boolean;
          section.active = (claim?.created as boolean) && !claim?.accepted;
          break;
        case PageSectionId.ContactDetails:
          section.completed =
            (claim.accepted as boolean) &&
            (claim?.callbackWidgetEnabled || !!claim?.serviceOptions);
          section.active =
            (claim.accepted as boolean) &&
            !claim?.callbackWidgetEnabled &&
            !claim?.serviceOptions;
          break;
        case PageSectionId.RepairOption:
          section.active =
            (claim.accepted as boolean) && !!claim?.serviceOptions;
          break;
        case PageSectionId.CallbackBookingWidget:
          section.active =
            (claim.accepted as boolean) && claim?.callbackWidgetEnabled;
          break;
      }
      return section;
    });
  }

  initSectionSchemaAndOptions(claim: Claim): {
    title: string;
    claimTypeOptions: Api.ClaimType[] | undefined;
    sectionSchema: PageSection[];
    isAnnualService: boolean;
  } {
    let sectionSchema: PageSection[] = this.sectionSchemaDefault;
    let claimTypeOptions = claim.getData?.ClaimTypes.filter((claimType) =>
      this.allClaimTypeCodes.includes(claimType.ClaimTypeCode)
    );
    let title = `Let’s get some help`;
    let isAnnualService = false;

    sectionSchema = sectionSchema.filter((ps: PageSection) => {
      return (
        ps.id !==
        (claim?.getData?.IWCallbackRequired
          ? PageSectionId.RepairOption
          : PageSectionId.CallbackBookingWidget)
      );
    });

    if (this._claimHelper.isASV(claim)) {
      isAnnualService = true;
      title = `Let's book your annual service`;
      claimTypeOptions = claim.getData?.ClaimTypes.filter(
        // filter to only Annual Service option
        (claimType) => claimType.ClaimTypeCode === 'ASV'
      );

      const sectionFaultDetailsQandA = sectionSchema.find(
        (s) => s.id === PageSectionId.FaultDetailsQandA
      );

      if (sectionFaultDetailsQandA) {
        sectionFaultDetailsQandA.title = 'Tell us the basics';
      }
    }

    if (this.hasASVrepairClaim(claim)) {
      const sectionClaimTypeAndFaultTypeQuestions = sectionSchema.find(
        (s) => s.id === PageSectionId.ClaimTypeAndFaultTypeQuestions
      );

      sectionClaimTypeAndFaultTypeQuestions.title = 'Describe the issue';
    }

    // filter out ASV claimType if plan has a separate ASV journey
    if (
      claim?.reflect?.asvOffered === 'true' &&
      claim?.reflect?.claimType === ClaimBundleType.REPAIR
    ) {
      claimTypeOptions = claim.getData?.ClaimTypes.filter(
        (claimType) => !['ASV'].includes(claimType.ClaimTypeCode)
      );
    }

    if (claimTypeOptions?.length === 0) {
      this.claimFacade.dispatch(
        claimActions.Error({
          payload: new Error(
            `No Claim Type options available after filtering for ${title}`
          ),
        })
      );
    }

    if (claimTypeOptions?.length === 1) {
      // only one Claim Type Option...

      const claimTypeAutoSelected = claimTypeOptions[0].ClaimTypeCode;
      const stopNoticeToBeDisplayed =
        this.showInWarrantyNotice(claim, claimTypeAutoSelected) ||
        this.showplanInWaitPeriodNotice(claim, claimTypeAutoSelected);

      const noFaultQuestionsToDisplay =
        !claim.getData?.FaultData ||
        (<Api.FaultData[]>claim.getData.FaultData).length === 0 ||
        ((<Api.FaultData[]>claim.getData.FaultData).length === 1 &&
          (claim?.getData?.FaultData as Api.FaultData[])[0]?.PossibleAnswers
            ?.length === 1) ||
        (<Api.FaultData>claim.getData.FaultData).PossibleAnswers?.length === 1;

      if (
        !stopNoticeToBeDisplayed &&
        (noFaultQuestionsToDisplay || isAnnualService)
      ) {
        sectionSchema = sectionSchema.filter(
          // filter out first page section
          (section) =>
            section.id !== PageSectionId.ClaimTypeAndFaultTypeQuestions
        );
      }
    }

    return { title, claimTypeOptions, sectionSchema, isAnnualService };
  }

  hasASVrepairClaim(claim: Claim) {
    return (
      claim?.getData?.ClaimTypes.some((c) => c.ClaimTypeCode === 'ASV') &&
      claim?.reflect.claimType === ClaimBundleType.REPAIR &&
      claim?.reflect.asvOffered === 'false'
    );
  }
}
